/*
 * Copyright (c) 2020.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Nikita Tropin <ntropin@yandex.ru>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Injectable, OnDestroy} from '@angular/core';
import {merge, MonoTypeOperatorFunction, Observable, Subject} from 'rxjs';
import {SubtaskLinkAction} from '../subtask-links-issue/subtask-link.action';
import {exhaustMap, filter, map, publishReplay, refCount, scan, startWith, tap} from 'rxjs/operators';
import {SubtaskLinkState} from '../subtask-links-issue/subtask-link.state';
import {TaskLinkService} from './task-link.service';
import {CrmIssueElastic} from 'compob2b-libs';

let defaultState: SubtaskLinkState = {
  items: [],
  loading: false
};

@Injectable({
  providedIn: 'root'
})
export class TaskLinkSearchStore implements OnDestroy {

  actions$: Subject<SubtaskLinkAction> = new Subject<SubtaskLinkAction>();

  load$: Observable<SubtaskLinkAction> =
    this.actions$.pipe(
      ofType('load'),
      exhaustMap((action: SubtaskLinkAction) => this.service.load(action.payload)),
      map(payload => ({
        type: 'load links success', payload
      }))
    );

  search$: Observable<SubtaskLinkAction> =
    this.actions$.pipe(
      ofType('search'),
      exhaustMap((action: SubtaskLinkAction) => this.service.search(action.payload)),
      map(payload => ({
        type: 'load success', payload
      }))
    );

  searchLast$: Observable<SubtaskLinkAction> =
    this.actions$.pipe(
      ofType('search last'),
      exhaustMap(_ => this.service.searchLast()),
      map(payload => ({type: 'load success', payload})),
      tap(data => defaultState = {items: data.payload, loading: false})
    );

  /**
   * Dispatcher
   */
  dispatcher$: Observable<SubtaskLinkAction> =
    merge(this.actions$, this.load$, this.search$, this.searchLast$);

  /**
   * State Reducer
   */
  state$: Observable<SubtaskLinkState | SubtaskLinkAction> =
    this.dispatcher$.pipe(
      //@ts-ignore
      startWith(defaultState),
      //@ts-ignore
      scan((state: SubtaskLinkState, action: SubtaskLinkAction): SubtaskLinkState => {
        switch (action.type) {
          case 'load':
          case 'search':
          case 'search last':
            return {...state, loading: true};
          case 'load success':
            return {...state, items: action.payload, loading: false};
          case 'reset':
            return defaultState;
          default:
            return state;
        }
      }),
      publishReplay(1),
      refCount()
    );

  items$: Observable<CrmIssueElastic[]> =
      //@ts-ignore
    this.state$.pipe(map((state: SubtaskLinkState) => state.items));
  count$: Observable<number> =
    this.items$.pipe(map((items: CrmIssueElastic[]) => items.length));
  loading$: Observable<boolean> =
    //@ts-ignore
    this.state$.pipe(map((state: SubtaskLinkState) => state.loading));

  constructor(private service: TaskLinkService) {
  }

  ngOnDestroy() {
    this.actions$.complete();
  }

  /**
   * Actions
   */

  load(id: number) {
    this.dispatch({type: 'load', payload: id});
  }

  search(keyword: string) {
    this.dispatch({type: 'search', payload: keyword});
  }

  searchLast() {
    this.dispatch({type: 'search last'});
  }

  reset() {
    this.dispatch({type: 'reset'});
  }

  private dispatch(action: SubtaskLinkAction): void {
    this.actions$.next(action);
  }
}

function ofType<T extends SubtaskLinkAction>(type: string): MonoTypeOperatorFunction<T> {
  return filter((_) => type === _.type);
}
